import { Plus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { createResource, extractErrorMessage } from "../../lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { SearchableSelect } from "./SearchableSelect";

const normalizeLocationPart = (value) => (value || "").trim().toLowerCase().replace(/\s+/g, " ");

export const buildLocationKey = (item) => [item.company_name, item.address, item.city, item.state_province, item.country].map(normalizeLocationPart).join("|");

export const matchSavedLocationId = (stop, locations) => {
  const stopKey = buildLocationKey(stop || {});
  if (!stopKey.replace(/\|/g, "")) {
    return "";
  }
  return locations.find((location) => buildLocationKey(location) === stopKey)?.id || "";
};

const buildLocationForm = (stop) => ({
  company_name: stop.company_name || "",
  contact_name: stop.contact_name || "",
  phone: stop.phone || "",
  address: stop.address || "",
  city: stop.city || "",
  state_province: stop.state_province || "",
  postal_code: stop.postal_code || "",
  country: stop.country || "Canada",
  notes: stop.notes || "",
});

export const SavedLocationSelector = ({
  allowCreate = false,
  compact = false,
  index,
  locations,
  onLocationCreated,
  onSelect,
  stop,
  testIdPrefix,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formState, setFormState] = useState(buildLocationForm(stop));

  useEffect(() => {
    setFormState(buildLocationForm(stop));
  }, [stop]);

  const currentLocationId = useMemo(() => stop.location_id || matchSavedLocationId(stop, locations), [locations, stop, stop.location_id]);
  const selectedLocation = useMemo(() => locations.find((location) => location.id === currentLocationId) || null, [currentLocationId, locations]);
  const displayLocation = selectedLocation || stop;
  const hasUnsavedLocation = !currentLocationId && buildLocationKey(stop).replace(/\|/g, "");

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const savedLocation = await createResource("/locations", {
        company_name: formState.company_name.trim(),
        contact_name: formState.contact_name.trim() || null,
        phone: formState.phone.trim() || null,
        address: formState.address.trim(),
        city: formState.city.trim(),
        state_province: formState.state_province.trim(),
        postal_code: formState.postal_code.trim() || null,
        country: formState.country.trim(),
        notes: formState.notes.trim() || null,
      });
      onLocationCreated(index, savedLocation);
      toast.success("Location saved.");
      setDialogOpen(false);
    } catch (error) {
      toast.error(extractErrorMessage(error, "Unable to save this location."));
    } finally {
      setSaving(false);
    }
  };

  const wrapperClass = compact ? "space-y-1" : "space-y-2";
  const labelClass = compact ? "text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500" : "text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500";
  const inputClass = compact ? "w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm" : "w-full rounded-2xl border border-zinc-300 bg-white px-4 py-3 text-sm";
  const readOnlyClass = compact ? "rounded-lg border border-zinc-300 bg-zinc-50 px-3 py-2 text-sm text-zinc-700" : "rounded-2xl border border-zinc-300 bg-zinc-50 px-4 py-3 text-sm text-zinc-700";
  const locationName = displayLocation.company_name || "Select or add a saved location";
  const locationAddress = [displayLocation.address, displayLocation.city, displayLocation.state_province, displayLocation.postal_code, displayLocation.country].filter(Boolean).join(", ") || "Select or add a saved location to continue.";
  const locationMeta = [displayLocation.contact_name, displayLocation.phone].filter(Boolean).join(" • ") || "Edit location details only from Records → Locations.";
  const locationOptions = [{ value: "", label: "Select saved location", searchText: "select saved location" }, ...locations.map((location) => ({
    value: location.id,
    label: `${location.company_name} — ${location.city || ""}`,
    meta: [location.address, location.state_province].filter(Boolean).join(", "),
    searchText: `${location.company_name} ${location.city || ""} ${location.address || ""}`,
  }))];

  return (
    <div className={`${wrapperClass} min-w-0`} data-testid={`${testIdPrefix}-location-block-${index}`}>
      <div className="flex items-end gap-2 min-w-0">
        <label className="block min-w-0 flex-1 space-y-1">
          <span className={labelClass}>Saved location</span>
          <SearchableSelect dataTestId={`${testIdPrefix}-saved-location-${index}`} onChange={(nextValue) => onSelect(index, nextValue)} options={locationOptions} placeholder="Select saved location" searchPlaceholder="Search locations..." triggerClassName={compact ? "rounded-lg px-3 py-2" : inputClass} value={currentLocationId || ""} />
        </label>
        {allowCreate ? (
          <button className={compact ? "inline-flex items-center gap-2 rounded-lg border border-zinc-300 bg-white px-3 py-2 text-xs font-semibold text-zinc-700 transition hover:border-zinc-900 hover:text-zinc-900" : "inline-flex items-center gap-2 rounded-2xl border border-zinc-300 bg-white px-4 py-3 text-sm font-semibold text-zinc-700 transition hover:border-zinc-900 hover:text-zinc-900"} data-testid={`${testIdPrefix}-add-location-${index}`} onClick={() => setDialogOpen(true)} type="button">
            <Plus className="h-4 w-4" /> {hasUnsavedLocation ? "Save location" : "Add location"}
          </button>
        ) : null}
      </div>

      <div className={`${readOnlyClass} min-w-0`} data-testid={`${testIdPrefix}-location-readonly-${index}`}>
        {!compact ? <p className="truncate text-sm font-semibold text-zinc-900" data-testid={`${testIdPrefix}-location-name-${index}`} title={locationName}>{locationName}</p> : null}
        <p className={`${compact ? "truncate text-xs font-medium text-zinc-700" : "mt-1 truncate text-xs text-zinc-600"}`} data-testid={`${testIdPrefix}-location-address-${index}`} title={locationAddress}>{locationAddress}</p>
        <p className="mt-1 truncate text-xs text-zinc-500" data-testid={`${testIdPrefix}-location-meta-${index}`} title={locationMeta}>{locationMeta}</p>
      </div>

      <Dialog onOpenChange={setDialogOpen} open={dialogOpen}>
        <DialogContent className="max-w-lg rounded-xl p-5" data-testid={`${testIdPrefix}-location-dialog-${index}`}>
          <DialogHeader>
            <DialogTitle>{hasUnsavedLocation ? "Save this location" : "Add new location"}</DialogTitle>
            <DialogDescription>Saved locations can be reused in Loads and Dispatch Board. Location edits later happen only in Records.</DialogDescription>
          </DialogHeader>

          <form className="grid gap-3 md:grid-cols-2" onSubmit={handleSubmit}>
            <label className="block space-y-1 md:col-span-2">
              <span className={labelClass}>Company</span>
              <input className={inputClass} onChange={(event) => setFormState((current) => ({ ...current, company_name: event.target.value }))} required type="text" value={formState.company_name} />
            </label>
            <label className="block space-y-1">
              <span className={labelClass}>Contact</span>
              <input className={inputClass} onChange={(event) => setFormState((current) => ({ ...current, contact_name: event.target.value }))} type="text" value={formState.contact_name} />
            </label>
            <label className="block space-y-1">
              <span className={labelClass}>Phone</span>
              <input className={inputClass} onChange={(event) => setFormState((current) => ({ ...current, phone: event.target.value }))} type="text" value={formState.phone} />
            </label>
            <label className="block space-y-1 md:col-span-2">
              <span className={labelClass}>Address</span>
              <input className={inputClass} onChange={(event) => setFormState((current) => ({ ...current, address: event.target.value }))} required type="text" value={formState.address} />
            </label>
            <label className="block space-y-1">
              <span className={labelClass}>City</span>
              <input className={inputClass} onChange={(event) => setFormState((current) => ({ ...current, city: event.target.value }))} required type="text" value={formState.city} />
            </label>
            <label className="block space-y-1">
              <span className={labelClass}>Province / State</span>
              <input className={inputClass} onChange={(event) => setFormState((current) => ({ ...current, state_province: event.target.value }))} required type="text" value={formState.state_province} />
            </label>
            <label className="block space-y-1">
              <span className={labelClass}>Postal code</span>
              <input className={inputClass} onChange={(event) => setFormState((current) => ({ ...current, postal_code: event.target.value }))} type="text" value={formState.postal_code} />
            </label>
            <label className="block space-y-1">
              <span className={labelClass}>Country</span>
              <input className={inputClass} onChange={(event) => setFormState((current) => ({ ...current, country: event.target.value }))} required type="text" value={formState.country} />
            </label>
            <label className="block space-y-1 md:col-span-2">
              <span className={labelClass}>Notes</span>
              <textarea className={`${inputClass} min-h-[84px]`} onChange={(event) => setFormState((current) => ({ ...current, notes: event.target.value }))} value={formState.notes} />
            </label>
            <div className="md:col-span-2 flex justify-end gap-2 pt-1">
              <button className="rounded-lg border border-zinc-300 px-4 py-2 text-sm font-semibold text-zinc-600" onClick={() => setDialogOpen(false)} type="button">Cancel</button>
              <button className="rounded-lg bg-zinc-900 px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-70" disabled={saving} type="submit">{saving ? "Saving..." : "Save location"}</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};